import React, { useState, useEffect } from 'react';
import { Calendar, Clock, X, CheckCircle, Loader2, User, Building, Mail, Phone, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { validateName, validateEmail, validatePhone, parseApiError } from '../utils/formValidation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BookingWidget({ isOpen, onClose, initialType = '' }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationType, setConsultationType] = useState(initialType || 'General Consultation');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [consultationTypes, setConsultationTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    project_brief: ''
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchInitialData = async () => {
    try {
      const res = await axios.get(`${API}/bookings/available-slots`);
      setConsultationTypes(res.data.consultation_types || []);
    } catch (e) {
      console.error('Failed to fetch booking data:', e);
    }
  };

  const fetchAvailableSlots = async (date) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings/available-slots?date=${date}`);
      setAvailableSlots(res.data.available_slots || []);
    } catch (e) {
      console.error('Failed to fetch slots:', e);
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    const validationError =
      validateName(formData.name) ||
      validateEmail(formData.email) ||
      validatePhone(formData.phone, { required: true });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: selectedDate,
        time_slot: selectedTime,
        consultation_type: consultationType
      });
      setSubmitted(true);
    } catch (e) {
      setError(parseApiError(e.response?.data?.detail, 'Failed to book. Please try again.'));
    }
    setSubmitting(false);
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty slots for days before first day
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days of month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const formatDateForApi = (date) => {
    return date.toISOString().split('T')[0];
  };

  const isDateSelectable = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Allow booking from tomorrow onwards, excluding weekends (Friday/Saturday in UAE)
    const dayOfWeek = date.getDay();
    return date > today && dayOfWeek !== 5 && dayOfWeek !== 6;
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" data-testid="booking-modal">
        {/* Header */}
        <div className="sticky top-0 bg-black text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Book a Consultation</h2>
            <p className="text-xs text-white/60">Schedule a meeting with our team</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 transition-colors" data-testid="close-booking">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        {!submitted && (
          <div className="px-6 py-4 border-b border-neutral-100">
            <div className="flex items-center justify-between">
              {['Date & Time', 'Details', 'Contact'].map((label, i) => (
                <div key={i} className="flex items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-black text-white' : 'bg-neutral-200 text-neutral-500'
                  }`}>
                    {step > i + 1 ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider hidden sm:block ${step === i + 1 ? 'text-black' : 'text-neutral-400'}`}>
                    {label}
                  </span>
                  {i < 2 && <div className="w-8 sm:w-12 h-px bg-neutral-200 mx-2" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Date & Time Selection */}
          {step === 1 && !submitted && (
            <div className="space-y-6">
              {/* Calendar */}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3 block">
                  <Calendar className="w-4 h-4 inline mr-2" />Select Date
                </label>
                
                <div className="border border-neutral-200 p-4">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-4">
                    <button 
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                      className="p-1 hover:bg-neutral-100"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="font-bold text-sm">
                      {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                    </span>
                    <button 
                      onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                      className="p-1 hover:bg-neutral-100"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Day Headers */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-center text-[10px] font-bold text-neutral-400 py-1">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {getDaysInMonth(currentMonth).map((date, i) => (
                      <button
                        key={i}
                        disabled={!isDateSelectable(date)}
                        onClick={() => date && setSelectedDate(formatDateForApi(date))}
                        className={`p-2 text-sm text-center transition-colors ${
                          !date ? '' :
                          !isDateSelectable(date) ? 'text-neutral-300 cursor-not-allowed' :
                          selectedDate === formatDateForApi(date) ? 'bg-black text-white font-bold' :
                          'hover:bg-neutral-100'
                        }`}
                      >
                        {date?.getDate() || ''}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-[10px] text-neutral-400 mt-2">Available: Sunday - Thursday</p>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3 block">
                    <Clock className="w-4 h-4 inline mr-2" />Select Time
                  </label>
                  
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <p className="text-sm text-neutral-500 py-4">No available slots for this date. Please select another date.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`px-3 py-2 text-xs font-medium border transition-colors ${
                            selectedTime === slot
                              ? 'border-black bg-black text-white'
                              : 'border-neutral-200 hover:border-neutral-400'
                          }`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => setStep(2)}
                disabled={!selectedDate || !selectedTime}
                className="w-full py-3 bg-black text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="booking-next-1"
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Consultation Details */}
          {step === 2 && !submitted && (
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3 block">
                  Consultation Type
                </label>
                <div className="space-y-2">
                  {consultationTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setConsultationType(type)}
                      className={`w-full p-3 text-left text-sm border transition-colors ${
                        consultationType === type
                          ? 'border-black bg-neutral-50 font-medium'
                          : 'border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">
                  <FileText className="w-4 h-4 inline mr-2" />Project Brief (Optional)
                </label>
                <textarea
                  value={formData.project_brief}
                  onChange={(e) => setFormData({ ...formData, project_brief: e.target.value })}
                  placeholder="Tell us about your project - materials, scale, timeline..."
                  rows={4}
                  className="w-full px-4 py-3 text-sm border border-neutral-200 focus:border-black focus:outline-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-3 border border-neutral-200 text-xs font-bold uppercase"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="flex-1 py-3 bg-black text-white text-xs font-bold uppercase tracking-wider"
                  data-testid="booking-next-2"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Details */}
          {step === 3 && !submitted && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-neutral-50 p-4 border border-neutral-200 mb-6">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Your Booking</p>
                <p className="text-sm font-bold">{selectedDate} at {selectedTime}</p>
                <p className="text-xs text-neutral-500">{consultationType}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-neutral-500 block mb-1">
                    <User className="w-3 h-3 inline mr-1" />Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 focus:border-black focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs text-neutral-500 block mb-1">
                    <Building className="w-3 h-3 inline mr-1" />Company
                  </label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-neutral-200 focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-neutral-500 block mb-1">
                  <Mail className="w-3 h-3 inline mr-1" />Email *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-neutral-200 focus:border-black focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="text-xs text-neutral-500 block mb-1">
                  <Phone className="w-3 h-3 inline mr-1" />Phone *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+971 50 XXX XXXX"
                  className="w-full px-3 py-2 text-sm border border-neutral-200 focus:border-black focus:outline-none"
                  required
                />
              </div>

              {error && (
                <p className="text-red-500 text-xs bg-red-50 p-3 border border-red-200">{error}</p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-3 border border-neutral-200 text-xs font-bold uppercase"
                >
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.name || !formData.email || !formData.phone}
                  className="flex-1 py-3 bg-black text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="booking-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
                  Confirm Booking
                </button>
              </div>
            </div>
          )}

          {/* Success */}
          {submitted && (
            <div className="text-center py-8" data-testid="booking-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold mb-2">Booking Confirmed!</h3>
              <p className="text-sm text-neutral-500 mb-6">
                We've sent a confirmation email to {formData.email}
              </p>
              
              <div className="bg-neutral-50 p-6 text-left inline-block">
                <p className="text-xs text-neutral-400 mb-2">Your appointment:</p>
                <p className="text-sm font-bold">{selectedDate} at {selectedTime}</p>
                <p className="text-xs text-neutral-500">{consultationType}</p>
              </div>

              <p className="text-xs text-neutral-400 mt-6">
                Our team will contact you within 24 hours to confirm details.
              </p>

              <button
                onClick={onClose}
                className="mt-6 px-6 py-2 bg-black text-white text-xs font-bold uppercase"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingWidget;
